import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { line, radius, font, surface } from 'features/dashboard/tokens'
import { scoreColour } from 'features/dashboard/charts/scoreColour'
import { NOT_ENOUGH_DATA } from 'features/actions/workspace/remedialPerformance'

const pct = v => {
  if (v === null || v === undefined) return NOT_ENOUGH_DATA
  if (v > 0 && Math.round(v) === 0) return '<1%'
  return `${Math.round(v)}%`
}

const SORTS = {
  total:      (a, b) => (b.total ?? 0) - (a.total ?? 0),
  completion: (a, b) => (b.completionRate ?? -1) - (a.completionRate ?? -1),
  onTime:     (a, b) => (b.onTimeRate ?? -1) - (a.onTimeRate ?? -1),
  overdue:    (a, b) => (b.overdue ?? 0) - (a.overdue ?? 0),
}

const DomainActivity = ({ domains = [], onOpenDomain }) => {
  const [sort, setSort] = useState('total')

  const rows = useMemo(() => [...domains].sort(SORTS[sort] || SORTS.total), [domains, sort])

  if (!domains.length) {
    return <Empty>No task activity by domain in this view yet.</Empty>
  }

  return (
    <Wrap>
      <SortBar>
        <SortLabel>Sort by</SortLabel>
        {[
          ['total', 'Busiest'],
          ['completion', 'Completion %'],
          ['onTime', 'On-time %'],
          ['overdue', 'Most overdue'],
        ].map(([k, label]) => (
          <SortChip key={k} type='button' $active={sort === k} onClick={() => setSort(k)}>
            {label}
          </SortChip>
        ))}
      </SortBar>

      <List>
        {rows.map(d => (
          <Row
            key={d.domainId}
            $clickable={!!onOpenDomain}
            onClick={onOpenDomain ? () => onOpenDomain(d) : undefined}
            title={onOpenDomain ? `Open ${d.name} tasks` : undefined}
          >
            <Dot style={{ background: d.colour || '#94a3b8' }} />
            <Main>
              <Name>{d.name}</Name>
              <Meta>
                {d.total} {d.total === 1 ? 'task' : 'tasks'} · {d.completed ?? 0} done
                {d.overdue > 0 ? ` · ${d.overdue} overdue` : ''}
                {d.completedLate > 0 ? ` · ${d.completedLate} late` : ''}
              </Meta>
              <Bar>
                <BarFill style={{ width: `${Math.max(2, d.completionRate ?? 0)}%`, background: scoreColour(d.completionRate) }} />
              </Bar>
            </Main>
            <Rates>
              <Rate $tint={scoreColour(d.completionRate)} title='Completion rate. Tasks completed ÷ total tasks in this domain.'>
                {pct(d.completionRate)}
              </Rate>
              <RateCaption>completed</RateCaption>
              <RateSub title='On-time rate. Of the tasks completed, the share finished on or before the deadline.'>
                {d.onTimeRate === null || d.onTimeRate === undefined ? 'No completed tasks yet' : `${pct(d.onTimeRate)} on time`}
              </RateSub>
            </Rates>
            {onOpenDomain && <OpenArrow aria-hidden='true'>→</OpenArrow>}
          </Row>
        ))}
      </List>
    </Wrap>
  )
}

export default DomainActivity

const Wrap = styled.div`
  display: flex; flex-direction: column; gap: 10px;
`

const SortBar = styled.div`
  display: inline-flex; align-items: center; gap: 4px;
  width: fit-content;
  padding: 3px 3px 3px 10px;
  border-radius: ${radius.pill};
  background: ${surface.wash};
  border: 1px solid ${line.base};
`

const SortLabel = styled.span`
  font-size: ${font.caption};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.grey[800]};
  margin-right: 2px;
`

const SortChip = styled.button`
  font: inherit;
  padding: 4px 11px;
  border-radius: ${radius.pill};
  border: none;
  cursor: pointer;
  font-size: ${font.caption};
  font-weight: 800;
  transition: all 160ms ease;
  ${({ $active, theme }) => $active
    ? `background: ${theme.colors.haiti}; color: #fff;`
    : `background: transparent; color: ${theme.colors.grey[800]}; &:hover { background: ${line.base}; }`}
`

const List = styled.div`
  display: flex; flex-direction: column;
`

const Row = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 11px 4px;
  border-bottom: 1px solid ${surface.wash};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: background 160ms ease;
  &:hover { background: ${({ $clickable }) => ($clickable ? surface.wash : 'transparent')}; }
  &:last-child { border-bottom: none; }
`

const Dot = styled.span`
  flex: 0 0 auto;
  width: 10px; height: 10px;
  border-radius: 50%;
`

const Main = styled.div`
  flex: 1; min-width: 0;
  display: flex; flex-direction: column; gap: 4px;
`

const Name = styled.span`
  font-size: ${font.body};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`

const Meta = styled.span`
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
  font-variant-numeric: tabular-nums;
`

const Bar = styled.div`
  height: 6px;
  background: ${({ theme }) => theme.colors.lighterGrey};
  border-radius: ${radius.pill};
  overflow: hidden;
`

const BarFill = styled.div`
  height: 100%;
  border-radius: ${radius.pill};
`

const Rates = styled.div`
  flex: 0 0 auto;
  display: flex; flex-direction: column; align-items: flex-end; gap: 2px;
  min-width: 74px;
`

const OpenArrow = styled.span`
  flex: 0 0 auto;
  font-size: 16px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.grey[600]};
  ${Row}:hover & { color: #33becc; }
`

const Rate = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 18px;
  font-variant-numeric: tabular-nums;
  color: ${({ $tint }) => $tint};
`

const RateCaption = styled.span`
  font-size: 9.5px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.grey[600]};
  margin-top: -2px;
`

const RateSub = styled.span`
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
  font-variant-numeric: tabular-nums;
`

const Empty = styled.div`
  padding: 28px;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: ${font.body};
  background: ${({ theme }) => theme.colors.lighterGrey};
  border: 1px dashed ${line.base};
  border-radius: ${radius.md};
`
